<template>
  <v-bottom-navigation
    v-model="activeItem"
    grow
    color="primary"
    data-testid="bottom-navigation"
  >
    <!-- Home button -->
    <v-btn
      v-if="homeItem"
      :value="homeItem.id"
      :data-testid="`nav-${homeItem.id}`"
      @click="handleNavigation(homeItem.path)"
    >
      <span style="font-size: 1.5rem">{{ homeItem.icon }}</span>
      <span>{{ homeItem.label }}</span>
    </v-btn>

    <!-- Actions menu -->
    <v-menu
      v-if="actionItems.length > 0"
      v-model="actionsMenuOpen"
      location="top"
      :close-on-content-click="true"
    >
      <template #activator="{ props: menuProps }">
        <v-btn v-bind="menuProps" value="actions" data-testid="nav-actions">
          <span style="font-size: 1.5rem">📋</span>
          <span>Actions</span>
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="item in actionItems"
          :key="item.id"
          :data-testid="`actions-${item.id}`"
          @click="handleActionNavigation(item.path)"
        >
          <template #prepend>
            <span style="font-size: 1.25rem; margin-right: 8px">{{
              item.icon
            }}</span>
          </template>
          <v-list-item-title>{{ item.label }}</v-list-item-title>
          <template v-if="item.badge" #append>
            <v-badge color="error" :content="item.badge" inline />
          </template>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Settings button -->
    <v-btn
      v-if="settingsItem"
      :value="settingsItem.id"
      :data-testid="`nav-${settingsItem.id}`"
      @click="handleNavigation(settingsItem.path)"
    >
      <span style="font-size: 1.5rem">{{ settingsItem.icon }}</span>
      <span>{{ settingsItem.label }}</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue'
import { useRoute } from 'vue-router'
import { useNavigation } from '../composables/useNavigation'
import { useRouterNavigation } from '../composables/useRouterNavigation'
import type { User } from '../api/types'

interface Props {
  user: User | null
}

const props = defineProps<Props>()
const route = useRoute()
const { navigateTo } = useRouterNavigation()

// Convert props.user to a ref for useNavigation composable
const userRef = toRef(props, 'user')
const { getNavItems } = useNavigation(userRef)

// Get navigation items from composable
const navItems = computed(() => getNavItems.value)

// Track active item based on current route
const activeItem = ref<string>('')

// Track actions menu state
const actionsMenuOpen = ref(false)

// Extract specific items from navigation
// For authenticated users, 'dashboard' is home; for unauthenticated, 'home' is home
const homeItem = computed(() =>
  navItems.value.find(item => item.id === 'dashboard' || item.id === 'home')
)
const settingsItem = computed(() =>
  navItems.value.find(item => item.id === 'settings')
)

// Action items (everything except home and settings)
const actionItems = computed(() => {
  return navItems.value.filter(
    item =>
      item.id !== 'dashboard' && item.id !== 'home' && item.id !== 'settings'
  )
})

// Update active item when route changes
watch(
  () => route.path,
  newPath => {
    const activeNav = navItems.value.find(item => item.path === newPath)
    if (activeNav) {
      activeItem.value = activeNav.id
    }
  },
  { immediate: true }
)

// Animation delay for menu close transitions (matches Vuetify default)
const MENU_CLOSE_DELAY_MS = 100

/**
 * Handle direct navigation (home, settings buttons)
 */
const handleNavigation = async (path: string) => {
  await navigateTo(path)
}

/**
 * Handle action menu navigation
 * Closes menu first, then navigates after animation completes
 */
const handleActionNavigation = async (path: string) => {
  // Close menu for better UX
  actionsMenuOpen.value = false

  // Wait for menu close animation before navigating
  setTimeout(() => {
    void navigateTo(path)
  }, MENU_CLOSE_DELAY_MS)
}
</script>
